import { Component, effect, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Filter } from '../../models/filter-models';
import { BaseTypeTier, DefenceType, EndgameContent, Rarity, WeaponType } from "../../models/enums";
import { ItemDataService } from '../../services/item-data.service';
import { FlatItemData, GearTierType, TieredGear } from '../../models/item-data-models';
import { ItemLabelComponent } from '../../common/item-label/item-label.component';
import { FormsModule } from '@angular/forms';
import { FilterService } from '../../services/filter.service';
import { GearTierService } from '../../services/tiering/gear-tier.service';
import { poe2 } from '../../../game';
import { FilterTitleComponent } from '../../common/filter-title/filter-title.component';
import { GearTierIconComponent } from '../../common/icons/gear-tier-icon/gear-tier-icon.component';
import { SelectComponent, SelectOptionComponent } from '../../common/select/select.component';
import { BaseComponent } from '../../common/base.component';
import { TooltipWrapperComponent } from '../../common/tooltip-wrapper/tooltip-wrapper.component';
import { EquipmentTierPickerComponent } from '../../common/equipment-tier-picker/equipment-tier-picker.component';
import { EquipmentType } from '../../models/equipment-type.enum';
import { DynamicContentComponent } from '../../common/dynamic-content/dynamic-content.component';
import { PresetsService } from '../../services/presets.service';
import { hasMovedFromOriginalTier } from '../tier-helpers';
import { GearTierNavigationTarget, NavigationService } from '../../services/navigation.service';
import { getLevelingLinkBand } from '../../services/generators/leveling-gear-generator.service';
import { collectMyGearWeaponClasses, pickPreferredWeaponClass, resolvePreferredWeaponClass } from '../../services/gear-preferred-weapon';

export type GearTierVm = {
  tier: GearTierType,
  tierStyle: string,
  tierSubtitle?: string,
  isDragOver?: boolean,
  dragCounter?: number;
  items: GearTierItemVm[],
}

const TIER_SUBTITLES: Record<GearTierType, string> = {
  Master: 'Valuable Endgame Bases',
  Elite: 'Popular Endgame Bases',
  Expert: 'Remaining Endgame Bases',
  Advanced: 'Late Campaign',
  Basic: 'Early Campaign',
};

export type GearTierItemVm = {
  name: string,
  image?: string,
  originalTier?: GearTierType,
  displayedAs?: DisplayedAsType,
  reason?: Reason,
  debugDropLevelBy5?: number,
  debugDiff?: number,
  restricted?: string,
  restrictedContent?: EndgameContent,
  socketGroup?: string,
  linkCount?: number,
};

type DisplayedAsType = 'Rare' | 'Normal' | 'Magic' | 'ExcellentSocket' | 'GoodSocket' | 'Never';
type Reason = 'DropLevelTooHigh' | 'AreaLevelTooLow' | 'AreaLevelTooHigh' | 'SpecialWeapon';

const ALL_ARMOUR = 'All Armour';
// PoE1: include Shields (full defence coverage). PoE2: body slots only — Shields/Bucklers stay separate.
const ALL_ARMOUR_ITEM_CLASSES = poe2
  ? ['Body Armours', 'Helmets', 'Gloves', 'Boots']
  : ['Body Armours', 'Helmets', 'Gloves', 'Boots', 'Shields'];

@Component({
  selector: 'app-gear-tiers',
  imports: [FormsModule, ItemLabelComponent, FilterTitleComponent, GearTierIconComponent, SelectComponent, SelectOptionComponent, TooltipWrapperComponent, EquipmentTierPickerComponent, DynamicContentComponent],
  templateUrl: './gear-tiers.component.html',
  styleUrls: ['./gear-tiers.component.scss', '../tier-tab-common.scss']
})
export class GearTiersComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input() changedOnly = false;

  itemData!: FlatItemData[];
  gearTierData!: TieredGear[];
  areas: Record<string, string> = {};

  fullItemClassList!: { type: string, image?: string }[];
  itemClassList!: { type: string, image?: string }[];

  itemClass!: string;
  defenceType?: string;
  areaLevel?: number;
  hasDropLevel = false;
  visibleDefenceTypes: string[] = [];
  visibleDefenceTypesForShields: string[] = [];

  selectedGearTierDataVm?: GearTierVm[];

  weaponClasses: string[] = [];
  armourClasses: string[] = [];
  defenceTypes = ['Arm', 'Eva', 'ES', 'Arm/Eva', 'Arm/ES', 'Eva/ES'];
  defenceTypesForShields = poe2 ? ['Arm', 'Arm/Eva', 'Arm/ES'] : ['Arm', 'Eva', 'ES', 'Arm/Eva', 'Arm/ES', 'Eva/ES'];

  tierStyleMap!: any;
  draggedItem: any | null = null;
  private navigationItemClass?: string;
  private navigationDefenceType?: string;


  BaseTypeTier = BaseTypeTier;
  EquipmentType = EquipmentType;
  Rarity = Rarity;

  constructor(
    private itemDataService: ItemDataService,
    private filterService: FilterService,
    private gearTierService: GearTierService,
    private navigationService: NavigationService,
  ) {
    super();
    this.weaponClasses = this.itemDataService.getAllWeaponTypes();
    this.armourClasses = this.itemDataService.getAllArmourTypes();
    this.itemData = this.itemDataService.getFlatItemData();
    this.areas = this.itemDataService.getAreas();
    this.visibleDefenceTypes = this.defenceTypes;
    this.visibleDefenceTypesForShields = this.defenceTypesForShields;

    effect(() => {
      this.itemDataService.getGearData()();
      setTimeout(() => {
        this.rebuildGearTierDataVm();
        this.hasDropLevel = this.gearTierData.some(g => g.dropLvl);
      });
    });

    effect(() => {
      const target = this.navigationService.gearTierNavigationTarget();
      if (!target) return;
      this.navigationService.gearTierNavigationTarget.set(null);
      this.applyNavigationTarget(target);
      setTimeout(() => this.navigationService.highlightBaseType(target.baseType), 0);
    });

    effect(() => {
      const limitToMyGear = this.settings.limitGearTierListToMyGear();
      if (!this.fullItemClassList) return;

      if (!limitToMyGear) {
        this.navigationItemClass = undefined;
        this.navigationDefenceType = undefined;
      }
      this.refreshVisibleDropdowns();
      this.ensureValidItemClassSelection();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] || changes['changedOnly']) {
      this.gearTierData = this.gearTierService.getGearTierData();

      this.tierStyleMap = (['Master', 'Elite', 'Expert', 'Advanced', 'Basic'] as GearTierType[]).reduce((acc, tier) => {
        acc[tier] = this.getTierStyle(tier);
        return acc;
      }, {} as any);

      const classImage = (itemType: string) => this.itemData.find(data => data.itemType === itemType)?.baseType.image!;
      this.fullItemClassList = [
        ...this.weaponClasses.map(i => ({ type: i, image: classImage(i) })),
        { type: ALL_ARMOUR, image: classImage('Body Armours') },
        ...this.armourClasses.map(i => ({ type: i, image: classImage(i) })),
      ];
      this.navigationItemClass = undefined;
      this.navigationDefenceType = undefined;
      this.refreshVisibleDropdowns();
      this.itemClass = this.getDefaultItemClass();

      this.changeItemClass();
    }
  }

  buildGearTierDataVm(): GearTierVm[] {
    return (['Master', 'Elite', 'Expert', 'Advanced', 'Basic'] as GearTierType[]).map((tier) => ({
        tier: tier,
        tierStyle: this.tierStyleMap[tier],
        tierSubtitle: TIER_SUBTITLES[tier],
        items: this.buildGearTierItemVm(tier)
      }));
  }

  buildGearTierItemVm(tier: GearTierType): GearTierItemVm[] {
    const gearTierItemVm: GearTierItemVm[] = [];

    this.gearTierData
      .filter((item) => item.tier === tier && this.matchesSelectedItemClass(item.itemClass))
      .filter((item) => {
        if (this.defenceType === undefined) return true;
        if (this.defenceType === 'Arm') return item.defenceType === DefenceType.Armour;
        if (this.defenceType === 'Eva') return item.defenceType === DefenceType.Evasion;
        if (this.defenceType === 'ES') return item.defenceType === DefenceType.EnergyShield;
        if (this.defenceType === 'Arm/Eva') return item.defenceType === DefenceType.ArmourEvasion;
        if (this.defenceType === 'Arm/ES') return item.defenceType === DefenceType.ArmourEnergyShield;
        if (this.defenceType === 'Eva/ES') return item.defenceType === DefenceType.EvasionEnergyShield;
        return false;
      })
      .forEach((tieredGear) => {
        const gearData: GearTierItemVm = { name: tieredGear.name, originalTier: tieredGear.originalTier };
        const flatItemData = this.itemDataService.getFlatItemDataByBaseTypeName(tieredGear.name);
        if (flatItemData) {
          gearData.image = flatItemData.baseType.image;
          gearData.restricted = flatItemData.baseType.restricted;
          gearData.restrictedContent = flatItemData.baseType.restrictedContent;
        }

        if (this.areaLevel && this.filter.showLevelingGear) {
          const itemClass = tieredGear.itemClass;
          if (poe2 && PresetsService.specialWeaponTypes.map(s => s.toString()).includes(itemClass)) {
            gearData.displayedAs = this.filter.showLevelingGearNormal
              ? 'Normal' : this.filter.showLevelingGearMagic
                ? 'Magic' : 'Rare';
            gearData.reason = 'SpecialWeapon';
            gearTierItemVm.push(gearData);
            return;
          }

          const areaLevel = this.areaLevel;
          const dropLevel = tieredGear.dropLvl ?? 0;
          const minAreaLevel = this.filter.showLevelingGearMinAreaLevel ?? 0;
          const maxAreaLevel = this.filter.showLevelingGearMaxAreaLevel ?? 80;

          // Check if item can't drop yet
          if (dropLevel > areaLevel) {
            gearData.displayedAs = 'Never';
            gearData.reason = 'DropLevelTooHigh';
          } else if (areaLevel < minAreaLevel) {
            gearData.displayedAs = 'Never';
            gearData.reason = 'AreaLevelTooLow';
          } else if (areaLevel > maxAreaLevel) {
            gearData.displayedAs = 'Never';
            gearData.reason = 'AreaLevelTooHigh';
          } else {
            // Bucketed system: we evaluate rules in 5-level buckets using the *upper* bound
            // Example (Normal, diff 5):
            //   Area 1-5  -> bucketUpper 5  -> minDrop 0  -> show all
            //   Area 6-10 -> bucketUpper 10 -> minDrop 5  -> drop >= 5
            //   Area 11-15 -> bucketUpper 15 -> minDrop 10 -> drop >= 10
            //   etc.
            const bucketUpperAreaLevel = Math.ceil(areaLevel / 5) * 5; // use upper edge of bucket

            // Compute the minimum drop level allowed for the current bucket and configured diff.
            // If diff is null/undefined, we treat it as "no restriction" (always show).
            const computeMinDropLevel = (configuredDiff: number | null | undefined): number | null => {
              if (configuredDiff === null || configuredDiff === undefined) {
                return null; // no restriction
              }
              return Math.max(0, bucketUpperAreaLevel - configuredDiff);
            };

            // Helper: check if the item's drop level meets the bucket+diff rule
            const checkLevelDifference = (configuredDiff: number | null | undefined): boolean => {
              const minDrop = computeMinDropLevel(configuredDiff);
              if (minDrop === null) return true;
              return dropLevel >= minDrop;
            };

            // Define all conditions in priority order with their check functions
            type ConditionCheck = {
              enabled: boolean;
              check: () => boolean;
              displayedAs: DisplayedAsType;
              socketGroup?: string;
            };

            // Determine max sockets for this item class
            // Quivers: no sockets
            // One-handed weapons: max 3 sockets
            // Boots, Helmets, Gloves: max 4 sockets
            // Everything else: max 6 sockets
            const oneHandWeaponTypes = this.itemDataService.getOneHandWeaponTypes().map(w => w.toString());
            const isNoSockets = itemClass === WeaponType.Quivers;
            const isOneHandWeapon = oneHandWeaponTypes.includes(itemClass);
            const isBootsHelmetGloves = ['Boots', 'Helmets', 'Gloves'].includes(itemClass);
            const maxSockets = isNoSockets ? 0 : isOneHandWeapon ? 3 : isBootsHelmetGloves ? 4 : 6;

            const linkBand = getLevelingLinkBand(areaLevel);
            const excellentLinkCount = linkBand.excellentLinks;
            const goodLinkCount = linkBand.goodLinks;
            const excellentSocketGroup = excellentLinkCount !== null ? 'W'.repeat(excellentLinkCount) : undefined;
            const goodSocketGroup = 'W'.repeat(goodLinkCount);

            // Only apply socketed conditions if link count doesn't exceed max sockets for this item class
            const excellentSocketApplies = excellentLinkCount !== null && excellentLinkCount <= maxSockets;
            const goodSocketApplies = goodLinkCount <= maxSockets;

            const conditions: ConditionCheck[] = [
              // 1. Normal
              {
                enabled: this.filter.showLevelingGearNormal,
                check: () => checkLevelDifference(this.filter.showLevelingGearNormalLevelDifference),
                displayedAs: 'Normal'
              },
              // 2. Magic
              {
                enabled: this.filter.showLevelingGearMagic,
                check: () => checkLevelDifference(this.filter.showLevelingGearMagicLevelDifference),
                displayedAs: 'Magic'
              },
              // 3. Rare
              {
                enabled: this.filter.showLevelingGearRare,
                check: () => checkLevelDifference(this.filter.showLevelingGearRareLevelDifference),
                displayedAs: 'Rare'
              },
            ];

            if (this.poe1) {
              conditions.push(
                // 4. Excellent Socketed
                {
                  enabled: this.filter.showLevelingGearExcellentSocketed && excellentSocketApplies,
                  check: () => {
                    if (this.filter.showLevelingGearExcellentSocketedDifference === null ||
                        this.filter.showLevelingGearExcellentSocketedDifference === undefined) {
                      return true;
                    }
                    return checkLevelDifference(this.filter.showLevelingGearExcellentSocketedDifference);
                  },
                  displayedAs: 'ExcellentSocket',
                  socketGroup: excellentSocketGroup
                },
                // 5. Good Socketed
                {
                  enabled: this.filter.showLevelingGearOtherSocketed && goodSocketApplies,
                  check: () => {
                    if (this.filter.showLevelingGearOtherSocketedLevelDifference === null ||
                        this.filter.showLevelingGearOtherSocketedLevelDifference === undefined) {
                      return true;
                    }
                    return checkLevelDifference(this.filter.showLevelingGearOtherSocketedLevelDifference);
                  },
                  displayedAs: 'GoodSocket',
                  socketGroup: goodSocketGroup
                }
              );
            }

            // Check conditions in priority order
            let displayedAs: DisplayedAsType | undefined = undefined;
            let socketGroup: string | undefined = undefined;

            for (const condition of conditions) {
              if (condition.enabled && condition.check()) {
                displayedAs = condition.displayedAs;
                socketGroup = condition.socketGroup;
                break; // Found highest priority match
              }
            }

            // If no condition matched, item is hidden
            if (displayedAs) {
              gearData.displayedAs = displayedAs;
              if (socketGroup) {
                gearData.socketGroup = socketGroup;
                gearData.linkCount = socketGroup.length;
              }
            } else {
              gearData.displayedAs = 'Never';
              gearData.reason = 'AreaLevelTooLow';
            }
          }
        }

        gearTierItemVm.push(gearData);
      });

    let result = gearTierItemVm;
    if (this.changedOnly) {
      result = gearTierItemVm.filter(it => hasMovedFromOriginalTier(it.originalTier, tier));
    }
    return result;
  }

  rebuildGearTierDataVm(): void {
    this.selectedGearTierDataVm = this.buildGearTierDataVm();
  }

  applyNavigationTarget(target: GearTierNavigationTarget): void {
    this.navigationItemClass = target.itemClass;
    this.navigationDefenceType = target.defenceType;
    this.refreshVisibleDropdowns();
    this.itemClass = target.itemClass;
    if (this.showsDefenceTypeSelector(target.itemClass)) {
      const types = this.getVisibleDefenceTypesForItemClass(target.itemClass);
      let defenceType = target.defenceType ?? types[0];
      if (!types.includes(defenceType)) {
        defenceType = types[0];
      }
      this.defenceType = defenceType;
    } else {
      this.defenceType = undefined;
    }
    this.rebuildGearTierDataVm();
  }

  toggleMyGearOnly(): void {
    this.settings.toggleLimitGearTierListToMyGear();
  }

  private ensureValidItemClassSelection(): void {
    if (!this.itemClassList.some(c => c.type === this.itemClass)) {
      this.itemClass = this.getDefaultItemClass();
      this.changeItemClass();
    } else if (this.defenceType) {
      const types = this.getVisibleDefenceTypesForItemClass();
      if (!types.includes(this.defenceType)) {
        this.changeItemClass();
      } else {
        this.rebuildGearTierDataVm();
      }
    } else {
      this.rebuildGearTierDataVm();
    }
  }

  changeItemClass(): void {
    if (!this.itemClass) return;
    if (!this.showsDefenceTypeSelector()) {
      this.defenceType = undefined;
    } else {
      this.defenceType = this.getVisibleDefenceTypesForItemClass()[0];
      this.changeDefenceType();
    }

    this.rebuildGearTierDataVm();
  }

  showsDefenceTypeSelector(itemClass = this.itemClass): boolean {
    if (itemClass === ALL_ARMOUR) return true;
    return this.armourClasses.includes(itemClass) && itemClass !== 'Bucklers';
  }

  changeDefenceType(): void {
    if (!this.defenceType) return;
    this.rebuildGearTierDataVm();
  }

  getTierStyle(gearTier: GearTierType) {
    const tierColor = gearTier === 'Basic' ? 'white' : gearTier === 'Advanced' ? 'yellow' : gearTier === 'Expert' ? 'orange' : gearTier === 'Elite' ? 'red' : 'purple';
    return 'tier-' + tierColor;
  }

  onDragStart(event: DragEvent, item: any) {
    this.draggedItem = item;
    event.dataTransfer?.setData('item', JSON.stringify(item));
  }

  onDragEnd() {
    this.draggedItem = null;
  }

  onDragEnter(event: DragEvent, tier: GearTierVm) {
    event.preventDefault();
    tier.dragCounter = (tier.dragCounter || 0) + 1;
    tier.isDragOver = true;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
  }

  onDragLeave(event: DragEvent, tier: GearTierVm) {
    tier.dragCounter = (tier.dragCounter || 0) - 1;
    if (tier.dragCounter === 0) {
      tier.isDragOver = false;
    }
  }

  onDrop(event: DragEvent, targetTier: any) {
    event.preventDefault();
    targetTier.dragCounter = 0;
    targetTier.isDragOver = false;
    const itemJson = event.dataTransfer!.getData('item');
    if (!itemJson) return;
    const item = JSON.parse(itemJson);
    this.filterService.updateGearItemTier(item.name, targetTier.tier);
    this.gearTierData = this.gearTierService.getGearTierData();
    this.selectedGearTierDataVm = this.buildGearTierDataVm();
    this.draggedItem = null;
  }

  resetToOriginalTier(item: any) {
    this.filterService.updateGearItemTier(item.name, item.originalTier);
    this.gearTierData = this.gearTierService.getGearTierData();
    this.selectedGearTierDataVm = this.buildGearTierDataVm();
  }

  hideInfoMessage() {
    this.settings.dismissGearTierListMessage();
  }

  private getDefaultItemClass(): string {
    // Prefer leveling / favourite weapons first — Quivers/Foci are deprioritized in the shared helper.
    const available = this.itemClassList.map(c => c.type);
    const fromWeapons = resolvePreferredWeaponClass(this.filter, available);
    if (fromWeapons) return fromWeapons;

    const activeArmourFilter = this.filter.armourFilters.find(a => a.show);
    if (activeArmourFilter?.armourType?.length) {
      const armourClass = activeArmourFilter.armourType[0].toString();
      if (this.itemClassList.some(c => c.type === armourClass)) {
        return armourClass;
      }
    }

    return pickPreferredWeaponClass(this.itemClassList.map(c => c.type), c => this.itemClassList.some(ic => ic.type === c))
      ?? this.itemClassList[0]?.type
      ?? this.fullItemClassList[0].type;
  }

  private refreshVisibleDropdowns(): void {
    const allowedWeaponClasses = this.settings.limitGearTierListToMyGear() ? this.getMyGearWeaponClasses() : null;
    const allowedDefenceTypes = this.settings.limitGearTierListToMyGear() ? this.getMyGearDefenceTypes() : null;

    this.itemClassList = this.fullItemClassList.filter(c => {
      if (!allowedWeaponClasses) return true;
      if (c.type === ALL_ARMOUR || this.armourClasses.includes(c.type)) return true;
      return allowedWeaponClasses.has(c.type) || c.type === this.navigationItemClass;
    });

    this.visibleDefenceTypes = this.filterDefenceTypes(this.defenceTypes, allowedDefenceTypes);
    this.visibleDefenceTypesForShields = this.filterDefenceTypesForShields(allowedDefenceTypes);
  }

  private matchesSelectedItemClass(itemClass: string): boolean {
    if (this.itemClass === ALL_ARMOUR) {
      return ALL_ARMOUR_ITEM_CLASSES.includes(itemClass);
    }
    return itemClass === this.itemClass;
  }

  private getVisibleDefenceTypesForItemClass(itemClass = this.itemClass): string[] {
    return itemClass === 'Shields' ? this.visibleDefenceTypesForShields : this.visibleDefenceTypes;
  }

  private filterDefenceTypes(types: string[], allowed: Set<string> | null): string[] {
    if (!allowed) return types;
    return types.filter(t => allowed.has(t) || t === this.navigationDefenceType);
  }

  private filterDefenceTypesForShields(allowed: Set<string> | null): string[] {
    if (!allowed) return this.defenceTypesForShields;
    const filtered = this.defenceTypesForShields.filter(t => allowed.has(t) || t === this.navigationDefenceType);
    return filtered.length > 0 ? filtered : this.defenceTypesForShields;
  }

  private getMyGearWeaponClasses(): Set<string> | null {
    return collectMyGearWeaponClasses(this.filter, this.weaponClasses);
  }

  private getMyGearDefenceTypes(): Set<string> | null {
    const types = new Set<string>();

    for (const armourFilter of this.filter.armourFilters) {
      if (!armourFilter.show) continue;
      for (const defenceType of armourFilter.defenceType) {
        if (defenceType !== DefenceType.All) {
          types.add(defenceType.toString());
        }
      }
    }

    if (this.filter.showLevelingGearArmour && this.filter.showLevelingGearArmourDefenceTypes?.length) {
      if (this.filter.showLevelingGearArmourDefenceTypes.includes(DefenceType.All)) {
        this.defenceTypes.forEach(t => types.add(t));
      } else {
        for (const defenceType of this.filter.showLevelingGearArmourDefenceTypes) {
          types.add(defenceType.toString());
        }
      }
    }

    return types.size > 0 ? types : null;
  }
}
